import React, { useState, useEffect, useCallback, useRef } from "react";
import { Auth } from "aws-amplify";
import { jwtDecode } from "jwt-decode";
import { useNavigate, useLocation } from "react-router-dom";
import { useLoading } from "../../../context/LoadingContext";
import { getParameterFromManager } from "../../../services/Utils";
import { v4 as uuidv4 } from "uuid";
import DataTable from "../../Requirement/components/common/ReusableTable";
import SearchFilter from "../../Requirement/components/common/SearchFilter";
import AlertModal from "../../Requirement/components/common/AlerModel";
import {
  fetchData,
  postQuery,
  searchfetchQueries,
} from "../../Requirement/services/RequirementServices";
import {
  FormControl,
  TextField,
  Typography,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
} from "@mui/material";

const ProcurementDocumentTypes = () => {
  const { setLoading } = useLoading();
  const location = useLocation();
  const navigate = useNavigate();
  const [rowStart, setRowStart] = useState(0);
  const [pageSize] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);
  const [activeUser, setActiveUser] = useState("");
  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    autoClose: null,
  });
  const [searchValues, setSearchValues] = useState({
    searchDocumentType: "",
  });
  const formRef = useRef(null);

  const [formData, setFormData] = useState({
    DocumentType: "",
  });

  const [errors, setErrors] = useState({});

  const handleModalClose = () => {
    setModal((prev) => ({ ...prev, show: false }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.DocumentType.trim()) {
      newErrors.DocumentType = "Document Type is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for the field being changed
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const saveDocumentType = async (data) => {
    console.log("data@@@@", data);
    try {
      const body = isEditMode
        ? {
            target: "updateDropdownValues",
            dropdownValuesInput: {
              ID: data.ID,
              Type: "ProcureDocType",
              Value: data.DocumentType,
            },
          }
        : {
            target: "createDropdownValues",
            dropdownValuesInput: {
              ID: data.ID,
              Type: "ProcureDocType",
              Value: data.DocumentType,
            },
          };
      console.log("body@@", body);
      const result = await postQuery(body);
      if (result) {
        setModal({
          show: true,
          type: "success",
          title: "Success",
          message: `Procurement Document Type ${
            isEditMode ? "updated" : "added"
          } successfully!`,
          autoClose: false,
        });
        await fetchDocumentTypes(searchValues);
      }
    } catch (error) {
      console.error("Error saving Procurement Document Type:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: `Failed to ${
          isEditMode ? "update" : "add"
        } Procurement Document Type.`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      const bodyList = {
        target: "listDropDownOptions",
        rowStart: 0,
        pageSize: 10000, // Large number to fetch all
      };

      const resultList = await fetchData(bodyList);
      let lastDropdownValuesCount=totalItems;
      if (resultList && (resultList.items || resultList.statusCode === 200)) {
        lastDropdownValuesCount=resultList.total||resultList.items.length;
      }

      await saveDocumentType({
        ...formData,
        ID: isEditMode ? selectedDocumentType.ID : lastDropdownValuesCount+1,
      });
      handleReset();
    }
  };

  const handleReset = () => {
    setFormData({
      DocumentType: "",
    });
    setErrors({});
    setIsEditMode(false);
    setSelectedDocumentType(null);
    setShowForm(false);
  };

  const fetchDocumentTypes = useCallback(
    async (searchValues, newRowStart = rowStart) => {
      setLoading(true);
      try {
        const body = {
          target: "listDropDownOptions",
          // filterTerms: { Type: "DocCategory" },
          filterTerms: { Type: "ProcureDocType" },
          rowStart: 0,
          pageSize: 10000, // Large number to fetch all
        };

        const result = await fetchData(body);
        console.log(result, "fetchoption");

        if (result && (result.items || result.statusCode === 200)) {
          const pdoctypes = result.items || [];
          setDocumentTypes(pdoctypes);
          setTotalItems(result.total || pdoctypes.length);
        } else {
          console.error("Error fetching filter options: ", result);
          setDocumentTypes([]);
          setTotalItems(0);
        }
      } catch (error) {
        console.error("Error fetching filter options: ", error);
        setDocumentTypes([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [rowStart, pageSize, setLoading]
  );

  const handleRowClick = (row) => {
    setSelectedDocumentType(row);
    setIsEditMode(true);
    setShowForm(true);
    setFormData({
      DocumentType: row.Value || "",
    });
  };

  useEffect(() => {
    fetchDocumentTypes(searchValues);
  }, [fetchDocumentTypes, searchValues]);

  useEffect(() => {
    if (showForm && formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [showForm]);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);

        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_ADMINS")) {
          await Auth.signOut();
          navigate("/");
        }
        await getParameterFromManager("INR_TO_USD");
      } catch (error) {
        await Auth.signOut();
        navigate("/");
      }
    };

    checkUser();
  }, [navigate]);

  const handlePageChange = (page) => {
    const newRowStart = (page - 1) * pageSize;
    setRowStart(newRowStart);
    fetchDocumentTypes(searchValues, newRowStart);
  };

  const handleSearch = (searchTerm) => {
    setSearchValues(searchTerm);
    setRowStart(0);
  };

  const columns = [{ id: "Value", numeric: false, label: "Document Type" }];

  const fields = [
    {
      name: "searchDocumentType",
      label: "Document Type",
      type: "text",
      value: searchValues.searchDocumentType,
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, searchDocumentType: value })),
    },
  ];

  return (
    <div className="amtpl-dashboard">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <div className="card">
        <div className="container-fluid">
          <div className="search-filter4">
            <div className="title4">
              <div className="head-text">Document Types</div>
            </div>
            <div className="filter4">
              <SearchFilter fields={fields} onSearch={handleSearch} />
            </div>
            <div
              className="head-btn"
              style={{ display: "flex", gap: "10px", alignItems: "center" }}
            >
              <div
                className="submit-btn"
                onClick={() => {
                  console.log("Add button clicked, setting showForm to true");
                  setShowForm(true);
                  setIsEditMode(false);
                  setFormData({
                    DocumentType: "",
                  });
                  setErrors({});
                }}
              >
                Add
              </div>
            </div>
          </div>
          <DataTable
            columns={columns}
            data={documentTypes}
            total={totalItems}
            title="Procurement Document Type List"
            rowsPerPage={pageSize}
            onRowClick={handleRowClick}
            onPageChange={handlePageChange}
            currentPage={Math.floor(rowStart / pageSize) + 1}
            selectedRows={
              selectedDocumentType ? [selectedDocumentType.DocumentTypeID] : []
            }
          />
          <div className="table-header">
            {showForm && (
              <div className="header-text">
                <div className="head-text">
                  {isEditMode ? "Modify" : "Add"} Procurement Document Type
                </div>
              </div>
            )}
            <div className="head-btn">
              {showForm && (
                <>
                  <div onClick={handleSubmit} className="submit-btn">
                    {isEditMode ? "Update" : "Submit"}
                  </div>
                  <div onClick={handleReset} className="submit-btn">
                    {isEditMode ? "Cancel" : "Reset"}
                  </div>
                </>
              )}
            </div>
          </div>

          {showForm && (
            <div className="row" ref={formRef}>
              <form onSubmit={handleSubmit} style={{ padding: "20px" }}>
                <div className="row">
                  <div className="col-md-6">
                    <label htmlFor="DocumentType">Document Type</label>
                    <TextField
                      id="DocumentType"
                      name="DocumentType"
                      value={formData.DocumentType}
                      onChange={handleChange}
                      fullWidth
                      required
                      error={!!errors.DocumentType}
                      helperText={errors.DocumentType}
                    />
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProcurementDocumentTypes;
